import '../styles/global/reset.scss';
import '../styles/global/base.scss';

import { CacheProvider, EmotionCache } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LicenseInfo } from '@mui/x-license-pro';
import ErrorMsgWrapper from 'atoms/Error/Messages/ErrorMsgWrapper';
import Header from 'atoms/Header';
import createEmotionCache from 'config/createEmotionCache';
import { burgerMenuItems, navigationBarItems, sideBarItems } from 'config/navigation';
import theme from 'config/theme';
import Alert from 'molecules/Alert';
import SideBar from 'molecules/SideBar';
import { AppProps as NextAppProps } from 'next/app';
import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import AuthProvider from 'organisms/AuthProvider';
import Footer from 'organisms/Footer';
import Navigation from 'organisms/Navigation';
import React, { useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { polyfill } from 'smoothscroll-polyfill';
import { initAnalytics, logPageView } from 'utils/analytics';
import { IAuthDetails, initAuth, verifyAuth } from 'utils/authUtils';
import { IHeaderConfig } from 'utils/globalTypes';
import { useScrollActions } from 'utils/hooks/useScrollActions';
import { useUserAuth } from 'utils/hooks/useUserAuth';
import { sameParent } from 'utils/urlUtils';

import SEO from '../../next-seo.config';
import s from './styles/PageStyles.module.scss';

const { publicRuntimeConfig } = getConfig();
const { NEXT_PUBLIC_MUI_KEY } = publicRuntimeConfig;

LicenseInfo.setLicenseKey(NEXT_PUBLIC_MUI_KEY);

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

interface CustomPageProps {
  authDetails?: IAuthDetails;
  headerConfig?: IHeaderConfig;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type AppProps<P = any> = {
  emotionCache?: EmotionCache;
  pageProps: P;
} & Omit<NextAppProps<P>, 'pageProps'>;

initAuth();
initAnalytics();

function App(props: AppProps<CustomPageProps>) {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  const authDetails = pageProps?.authDetails;
  const headerConfig = pageProps?.headerConfig;

  const { isSticky } = useScrollActions(undefined, 0);

  const modalContainer = useRef<HTMLDivElement>(null);

  const router = useRouter();

  const isShowHeader = !router?.pathname?.startsWith('/connect');
  const isShowSideBar = !!sideBarItems.filter(({ url }) => sameParent(router, url)).length;

  const clientAuthDetails = useUserAuth();

  useEffect(() => {
    verifyAuth(authDetails, clientAuthDetails);
  }, [authDetails, clientAuthDetails]);

  useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles && jssStyles.parentElement) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
    polyfill();
  }, []);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      logPageView(url);
    };
    router?.events?.on('routeChangeComplete', handleRouteChange);
    return () => {
      router?.events?.off('routeChangeComplete', handleRouteChange);
    };
  }, [router?.events]);

  const generateMainSection = (errorCode?: string | number) => {
    return (
      <>
        <NextSeo {...SEO} noindex nofollow />
        <Head>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Head>
        {isShowHeader && (
          <>
            <Alert />
            <Header
              isManagement={authDetails?.isManagement}
              isSticky={isSticky}
              pathName={router?.pathname}
              headerConfig={headerConfig}
            >
              <Navigation
                burgerMenuItems={burgerMenuItems}
                navigationBarItems={navigationBarItems}
                sideBarItems={sideBarItems}
                isSticky={isSticky}
                headerConfig={headerConfig}
                authDetails={authDetails}
              />
            </Header>
          </>
        )}

        {errorCode ? (
          <ErrorMsgWrapper errorCode={errorCode} />
        ) : (
          <section className={s.PageGlobal}>
            {isShowSideBar && (
              <aside>
                <SideBar isManagement={authDetails?.isManagement} navItems={sideBarItems} />
              </aside>
            )}
            <section className={s.PageGlobal__section}>
              <Component {...pageProps} />
            </section>
            <div id="modalContainer" ref={modalContainer} />
          </section>
        )}
        <Footer />
      </>
    );
  };

  const ErrorFallback = () => generateMainSection('500');

  return (
    <React.StrictMode>
      <CacheProvider value={emotionCache}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
            <CssBaseline />
            <AuthProvider authDetails={authDetails}>
              <ErrorBoundary
                FallbackComponent={ErrorFallback} //prop required by npm package
                onError={(e) => console.log('Error', e)}
              >
                {generateMainSection()}
              </ErrorBoundary>
            </AuthProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </CacheProvider>
    </React.StrictMode>
  );
}

export default App;
